import React, { useState } from "react";

/**
 * Classify one or many transactions with a governed category + rationale.
 * Backend contract: PATCH /api/transactions/{id}/reclassify {classification, reason}
 * Available categories are listed below; adding a new one requires a
 * backend-side allow-list update if enforcement is added there.
 */
const CLASSIFICATIONS = [
  { value: "batch_journal",       label: "Batch journal",       hint: "The GL side is a monthly batch journal that aggregates many daily bank items. Use when the counterpart is expected but netted across multiple bank rows." },
  { value: "deposit_in_transit",  label: "Deposit in transit",  hint: "Bank has it, GL doesn't yet — recorded across month-end." },
  { value: "outstanding_cheque",  label: "Outstanding cheque",  hint: "GL has it, bank hasn't cleared yet." },
  { value: "bank_fee",            label: "Bank fee",            hint: "Bank-only charge normally booked via monthly journal." },
  { value: "interest",            label: "Interest",            hint: "Interest earned or charged; bank-only until booked." },
  { value: "bsje_candidate",      label: "BSJE candidate",      hint: "Needs a Booked Standard Journal Entry — flag for later posting." },
  { value: "timing_difference",   label: "Timing difference",   hint: "Other timing item not covered by the categories above." },
  { value: "other",               label: "Other",               hint: "Doesn't fit any category — rationale is mandatory." },
];

export function ClassifyDialog({ transactions, onConfirm, onCancel }) {
  const [category, setCategory] = useState("batch_journal");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const rows = transactions || [];
  const chosen = CLASSIFICATIONS.find((c) => c.value === category);
  const canSubmit = reason.trim().length > 0 && !submitting && rows.length > 0;

  if (rows.length === 0) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ category, reason: reason.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="classify-dialog"
         style={{ position: "fixed", inset: 0, background: "rgba(15,20,30,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
         onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <form onSubmit={submit}
            style={{ background: "#fff", maxWidth: 540, width: "92%", borderRadius: 8, padding: "22px 24px", boxShadow: "0 12px 36px rgba(0,0,0,0.18)", fontFamily: "system-ui, -apple-system, sans-serif", color: "#1f2937" }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
          Classify {rows.length > 1 ? `${rows.length} transactions` : "transaction"}
        </h3>
        {rows.length === 1 && (
          <p style={{ marginTop: 6, marginBottom: 14, fontSize: 12.5, color: "#4b5563" }}>
            <b>{(rows[0].description || "").slice(0, 90)}</b>
            {" · "}${Number(rows[0].amount_unsigned || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {" · "}{rows[0].dr_cr}
          </p>
        )}
        {rows.length > 1 && (
          <p style={{ marginTop: 6, marginBottom: 14, fontSize: 12.5, color: "#4b5563" }}>
            Applies the same category + rationale to every selected row. Individual audit-log entries are still written per row.
          </p>
        )}

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="classify-category" style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
            Category
          </label>
          <select id="classify-category"
                  data-testid="classify-category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", fontSize: 14, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff" }}>
            {CLASSIFICATIONS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {chosen?.hint && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "#6b7280", lineHeight: 1.45 }}>
              {chosen.hint}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="classify-reason" style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
            Reason (required)
          </label>
          <textarea id="classify-reason"
                    data-testid="classify-reason-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus rows={3}
                    placeholder="Explain the classification decision. This is written to the audit trail."
                    style={{ width: "100%", boxSizing: "border-box", fontSize: 13, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", resize: "vertical" }} />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" data-testid="classify-cancel-btn" onClick={onCancel} disabled={submitting}
                  style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, cursor: submitting ? "not-allowed" : "pointer" }}>
            Cancel
          </button>
          <button type="submit" data-testid="classify-confirm-btn" disabled={!canSubmit}
                  style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: canSubmit ? "#166534" : "#9ca3af", color: "#fff", fontSize: 13, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed" }}>
            {submitting ? "Classifying…" : `Classify ${rows.length > 1 ? `${rows.length} rows` : ""}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClassifyDialog;
